import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Typography, Skeleton } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import cn from 'classnames'
import { isEmpty } from 'lodash-es'

import { replaceWhatsappBody } from 'utils/helper'
import WhatsAppPreview from './WhatsAppPreview'
import PhoneNumber from './PhoneNumber'
import styles from './whatsapp-method.module.scss'
import WhatsAppPersonalNumberCard from './WhatsAppPersonalNumberCard'

const { Text } = Typography

const URL_REGEX = /^(https?:\/\/[^\s]+)$/i
const PHONE_REGEX = /^[+]?[\d\s()-]{7,}$/

const InfoMessage = () => {
  const { formatMessage } = useIntl()

  return (
    <div className={styles.infoMessage}>
      <InfoCircleOutlined className={styles.infoIcon} />
      <Text className={styles.infoText}>
        {formatMessage({ id: 'financeInvoices.whatsapp_template_info' })}
      </Text>
    </div>
  )
}

const WhatsAppMethod = ({
  data,
  loading,
  isMass,
  getWaTemplate,
  goToWaTamplate,
  transTypeId,
  waTemplateKey,
  onClose,
}) => {
  const formatBody = useMemo(() => {
    if (!data) {
      return {
        text: '',
        jsx: [],
      }
    }

    const { content } = data.payload
    const defaultValues = data.default_value

    const parts = !isEmpty(content) ? content.split(/(\{\{\d+}})/) : []
    const jsxParts = []
    const textParts = []

    parts.forEach((part, index) => {
      const match = part.match(/\{\{(\d+)}}/)
      if (match) {
        const _value = parseInt(match[1], 10) - 1
        const defaultValue = defaultValues[_value]
        textParts.push(defaultValue)
        jsxParts.push(
          <>
            {URL_REGEX.test(defaultValue) ? (
              <a
                href={defaultValue}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.urlMessage}
              >
                {defaultValue}
              </a>
            ) : PHONE_REGEX.test(defaultValue) ? (
              <a
                href={`tel:${defaultValue.replace(/[\s()-]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.phoneNumberMessage}
              >
                {defaultValue}
              </a>
            ) : (
              <span>{defaultValue}</span>
            )}
          </>,
        )
        return
      }

      textParts.push(part)

      // For non-placeholder text, split by newlines and add <br /> tags
      jsxParts.push(
        <>
          {part.split('\n').map((line, lineIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${index}-${lineIndex}`}>
              {lineIndex > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </>,
      )
    })

    return {
      text: replaceWhatsappBody(textParts.join('')),
      jsx: jsxParts,
    }
  }, [data])

  return (
    <Skeleton active loading={loading}>
      <div className={cn(styles.formSectionContainer, isMass && styles.formSectionContainerMass)}>
        {!isMass ? (
          <div className={styles.formSection}>
            <PhoneNumber />

            <WhatsAppPersonalNumberCard
              data={data}
              getWaTemplate={getWaTemplate}
              goToWaTamplate={goToWaTamplate}
              transTypeId={transTypeId}
              waTemplateKey={waTemplateKey}
              onClose={onClose}
            />

            <InfoMessage />
          </div>
        ) : null}

        <WhatsAppPreview isMass={isMass}>
          {formatBody.jsx.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>{item}</React.Fragment>
          ))}
        </WhatsAppPreview>
        {isMass && <InfoMessage />}
      </div>
    </Skeleton>
  )
}

export default WhatsAppMethod
